import React, { useState } from "react";

import SaveSignalsModal, {
  HIDE_SAVE_CPV_CODE_MODAL,
  HIDE_SAVE_KEYWORD_MODAL,
} from "components/modals/SaveSignalsModal";
import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { appRoutes } from "lib/appRoutes";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { useUpdateSignalSettings } from "lib/hooks/api/signals/useUpdateSignalSettings";
import { EventNames, useTracking } from "lib/tracking";
import { capitalize, simpleArrayDedupe } from "lib/utils";

type Props = {
  signalType: "keyword" | "CPV code";
  existingSignalValues: string[];
  addedSignalValues: string[];
};
export default function SaveSignalsButton({
  signalType,
  existingSignalValues,
  addedSignalValues,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const { logEvent } = useTracking();

  const { mutate: updateSignalSettings, isLoading: isUpdatingSignalSettings } =
    useUpdateSignalSettings({
      onSuccess: (_) => {
        logEvent(EventNames.recordQueryUpdated, {
          "Context source": `${capitalize(signalType)} filter`,
          "Action type": "Added",
          [`New ${signalType}s`]: addedSignalValues,
          "Signal type": capitalize(signalType),
        });
        toast.success("Signal settings updated", {
          action: isRelevancySearchEnabled ? undefined : (
            <Button as="a" icon="share03" {...appRoutes.feedSettings}>
              View signal settings
            </Button>
          ),
        });
      },
      onError: (_) => {
        toast.error(
          `Failed to update ${signalType} signals. Please contact an admin if this issue persists.`,
        );
      },
    });

  const updateSignals = (newSignalVals: string[]) => {
    if (signalType === "keyword") {
      updateSignalSettings({ input: { keywords: newSignalVals } });
    } else if (signalType === "CPV code") {
      updateSignalSettings({ input: { cpvCodes: newSignalVals } });
    }
  };

  /**
   * Depending on user's history, either opens an informative modal, or
   *  just saves/unsaves the free text keyword(s) immediately
   * @param type
   * @param savedGuids
   */
  const saveSignalValues = () => {
    // Make sure we maintain the previous keywords and add the new free-text ones
    const newSignalVals = simpleArrayDedupe(existingSignalValues.concat(addedSignalValues));
    const hideModalKey =
      signalType === "keyword" ? HIDE_SAVE_KEYWORD_MODAL : HIDE_SAVE_CPV_CODE_MODAL;

    if (localStorage.getItem(hideModalKey) === "true") {
      updateSignals(newSignalVals);
    } else {
      setModalOpen(true);
    }
  };

  return (
    <>
      <Button onClick={saveSignalValues} loading={isUpdatingSignalSettings} icon="signal01">
        Save as signals
      </Button>
      <SaveSignalsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        signalType={signalType}
        onConfirm={() => {
          const newSignalVals = simpleArrayDedupe(existingSignalValues.concat(addedSignalValues));
          updateSignals(newSignalVals);
        }}
      />
    </>
  );
}
